import { useState, useEffect } from "react";
import { useAuth, useLanguage, API } from "../App";
import { AppLayout } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Shield, Plus, AlertTriangle, ClipboardCheck, Search, Trash2, CheckCircle,
  Eye, Pencil, ArrowRight, Loader2, CalendarDays, User, MapPin
} from "lucide-react";
import { toast } from "sonner";

export const KMAPage = () => {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [riskAssessments, setRiskAssessments] = useState([]);
  const [safetyRounds, setSafetyRounds] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [raDialogOpen, setRaDialogOpen] = useState(false);
  const [srDialogOpen, setSrDialogOpen] = useState(false);
  const [detailRA, setDetailRA] = useState(null);
  const [detailSR, setDetailSR] = useState(null);
  const [editingRA, setEditingRA] = useState(false);
  const [editingSR, setEditingSR] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Forms
  const emptyRisk = { hazard: "", consequence: "", probability: "medium", severity: "medium", mitigation: "" };
  const emptyFinding = { description: "", severity: "medium", responsible: "", due_date: "" };
  const [raForm, setRaForm] = useState({ title: "", description: "", location: "", work_type: "", responsible: "", risks: [{ ...emptyRisk }] });
  const [srForm, setSrForm] = useState({ title: "", date: new Date().toISOString().split("T")[0], participants: "", area: "", findings: [{ ...emptyFinding }] });

  const sv = (s, e) => language === "sv" ? s : e;
  const hdrs = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const authHdr = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchProjects(); fetchSummary(); }, []);
  useEffect(() => { if (selectedProject) { fetchRA(); fetchSR(); } }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API}/projects`, { headers: authHdr, credentials: "include" });
      if (res.ok) { const d = await res.json(); setProjects(d); if (d.length > 0) setSelectedProject(d[0].project_id); }
    } catch {} finally { setLoading(false); }
  };
  const fetchSummary = async () => {
    try { const res = await fetch(`${API}/kma/summary`, { headers: authHdr, credentials: "include" }); if (res.ok) setSummary(await res.json()); } catch {}
  };
  const fetchRA = async () => {
    try { const res = await fetch(`${API}/kma/projects/${selectedProject}/risk-assessments`, { headers: authHdr, credentials: "include" }); if (res.ok) setRiskAssessments(await res.json()); } catch {}
  };
  const fetchSR = async () => {
    try { const res = await fetch(`${API}/kma/projects/${selectedProject}/safety-rounds`, { headers: authHdr, credentials: "include" }); if (res.ok) setSafetyRounds(await res.json()); } catch {}
  };

  // RA CRUD
  const handleCreateRA = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const res = await fetch(`${API}/kma/projects/${selectedProject}/risk-assessments`, { method: "POST", headers: hdrs, credentials: "include", body: JSON.stringify(raForm) });
      if (res.ok) { toast.success(sv("Riskbedömning skapad!", "Risk assessment created!")); setRaDialogOpen(false); resetRAForm(); fetchRA(); fetchSummary(); }
      else toast.error(sv("Kunde inte skapa", "Could not create"));
    } catch { toast.error(sv("Ett fel uppstod", "An error occurred")); } finally { setSubmitting(false); }
  };
  const handleUpdateRA = async (raId, data) => {
    try {
      const res = await fetch(`${API}/kma/risk-assessments/${raId}`, { method: "PUT", headers: hdrs, credentials: "include", body: JSON.stringify(data) });
      if (res.ok) { const updated = await res.json(); setDetailRA(updated); fetchRA(); fetchSummary(); return true; }
    } catch {} return false;
  };
  const handleDeleteRA = async (raId) => {
    if (!window.confirm(sv("Vill du radera denna riskbedömning?", "Delete this risk assessment?"))) return;
    try {
      await fetch(`${API}/kma/risk-assessments/${raId}`, { method: "DELETE", headers: authHdr, credentials: "include" });
      toast.success(sv("Raderad", "Deleted")); setDetailRA(null); fetchRA(); fetchSummary();
    } catch { toast.error(sv("Kunde inte radera", "Could not delete")); }
  };

  // SR CRUD
  const handleCreateSR = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const body = { ...srForm, participants: srForm.participants.split(",").map(p => p.trim()).filter(Boolean) };
      const res = await fetch(`${API}/kma/projects/${selectedProject}/safety-rounds`, { method: "POST", headers: hdrs, credentials: "include", body: JSON.stringify(body) });
      if (res.ok) { toast.success(sv("Skyddsrond skapad!", "Safety round created!")); setSrDialogOpen(false); resetSRForm(); fetchSR(); fetchSummary(); }
      else toast.error(sv("Kunde inte skapa", "Could not create"));
    } catch { toast.error(sv("Ett fel uppstod", "An error occurred")); } finally { setSubmitting(false); }
  };
  const handleUpdateSR = async (srId, data) => {
    try {
      const res = await fetch(`${API}/kma/safety-rounds/${srId}`, { method: "PUT", headers: hdrs, credentials: "include", body: JSON.stringify(data) });
      if (res.ok) { const updated = await res.json(); setDetailSR(updated); fetchSR(); fetchSummary(); return true; }
    } catch {} return false;
  };
  const handleUpdateFinding = async (srId, findingId, data) => {
    try {
      const res = await fetch(`${API}/kma/safety-rounds/${srId}/findings/${findingId}`, { method: "PUT", headers: hdrs, credentials: "include", body: JSON.stringify(data) });
      if (res.ok) {
        // Refresh detail
        const srRes = await fetch(`${API}/kma/safety-rounds/${srId}`, { headers: authHdr, credentials: "include" });
        if (srRes.ok) setDetailSR(await srRes.json());
        fetchSR(); fetchSummary();
        return true;
      }
    } catch {} return false;
  };
  const handleDeleteSR = async (srId) => {
    if (!window.confirm(sv("Vill du radera denna skyddsrond?", "Delete this safety round?"))) return;
    try {
      await fetch(`${API}/kma/safety-rounds/${srId}`, { method: "DELETE", headers: authHdr, credentials: "include" });
      toast.success(sv("Raderad", "Deleted")); setDetailSR(null); fetchSR(); fetchSummary();
    } catch { toast.error(sv("Kunde inte radera", "Could not delete")); }
  };

  const resetRAForm = () => setRaForm({ title: "", description: "", location: "", work_type: "", responsible: "", risks: [{ ...emptyRisk }] });
  const resetSRForm = () => setSrForm({ title: "", date: new Date().toISOString().split("T")[0], participants: "", area: "", findings: [{ ...emptyFinding }] });

  // Risk form helpers
  const addRisk = () => setRaForm({ ...raForm, risks: [...raForm.risks, { ...emptyRisk }] });
  const updateRisk = (idx, field, val) => { const r = [...raForm.risks]; r[idx] = { ...r[idx], [field]: val }; setRaForm({ ...raForm, risks: r }); };
  const removeRisk = (idx) => setRaForm({ ...raForm, risks: raForm.risks.filter((_, i) => i !== idx) });
  const addFinding = () => setSrForm({ ...srForm, findings: [...srForm.findings, { ...emptyFinding }] });
  const updateFinding = (idx, field, val) => { const f = [...srForm.findings]; f[idx] = { ...f[idx], [field]: val }; setSrForm({ ...srForm, findings: f }); };
  const removeFinding = (idx) => setSrForm({ ...srForm, findings: srForm.findings.filter((_, i) => i !== idx) });

  // Style helpers
  const sevColors = { critical: "bg-red-500 text-white", high: "bg-orange-500 text-white", medium: "bg-amber-100 text-amber-800", low: "bg-blue-100 text-blue-800" };
  const sevLabels = { critical: sv("Kritisk", "Critical"), high: sv("Hög", "High"), medium: sv("Medel", "Medium"), low: sv("Låg", "Low") };
  const statusLabels = { draft: sv("Utkast", "Draft"), under_review: sv("Under granskning", "Under Review"), approved: sv("Godkänd", "Approved"), completed: sv("Slutförd", "Completed"), open: sv("Öppen", "Open"), in_progress: sv("Pågår", "In Progress"), resolved: sv("Åtgärdad", "Resolved"), closed: sv("Stängd", "Closed") };
  const statusColors = { draft: "bg-slate-100 text-slate-700", under_review: "bg-blue-100 text-blue-700", approved: "bg-green-100 text-green-700", completed: "bg-green-100 text-green-700", open: "bg-amber-100 text-amber-800", in_progress: "bg-blue-100 text-blue-700", resolved: "bg-green-100 text-green-700", closed: "bg-slate-100 text-slate-600" };

  const riskLevelColor = (level) => ({ critical: "bg-red-600 text-white", high: "bg-orange-500 text-white", medium: "bg-amber-400 text-amber-900", low: "bg-green-400 text-green-900" }[level] || "bg-slate-200");
  const riskLevelLabel = (level) => ({ critical: sv("Kritisk", "Critical"), high: sv("Hög", "High"), medium: sv("Medel", "Medium"), low: sv("Låg", "Low") }[level] || level);

  const bySeverity = summary?.incidents_by_severity || {};
  const totalInc = Object.values(bySeverity).reduce((a, b) => a + b, 0);

  const tabs = [
    { id: "overview", label: sv("Översikt", "Overview"), icon: Shield },
    { id: "risk", label: sv("Riskbedömningar", "Risk Assessments"), icon: AlertTriangle },
    { id: "safety", label: sv("Skyddsronder", "Safety Rounds"), icon: ClipboardCheck },
  ];

  return (
    <AppLayout>
      <div className="p-4 lg:p-6 space-y-6 pb-20 lg:pb-6" data-testid="kma-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>KMA</h1>
            <p className="text-slate-600 mt-1">{sv("Kvalitet, Miljö & Arbetsmiljö", "Quality, Environment & Safety")}</p>
          </div>
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-full sm:w-[280px]" data-testid="kma-project-select"><SelectValue placeholder={sv("Välj projekt", "Select project")} /></SelectTrigger>
            <SelectContent>{projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
          {tabs.map(tb => (
            <button key={tb.id} onClick={() => setTab(tb.id)} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === tb.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-testid={`kma-tab-${tb.id}`}>
              <tb.icon className="w-4 h-4" /> {tb.label}
            </button>
          ))}
        </div>

        {/* OVERVIEW TAB */}
        {tab === "overview" && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <Card><CardContent className="p-4"><div className="p-2 rounded-lg bg-purple-500 w-fit"><Shield className="w-5 h-5 text-white" /></div><p className="text-2xl font-bold mt-3" style={{ fontFamily: 'Barlow Condensed' }}>{summary?.risk_assessments || 0}</p><p className="text-sm text-slate-600">{sv("Riskbedömningar", "Risk Assessments")}</p></CardContent></Card>
              <Card><CardContent className="p-4"><div className="p-2 rounded-lg bg-teal-500 w-fit"><ClipboardCheck className="w-5 h-5 text-white" /></div><p className="text-2xl font-bold mt-3" style={{ fontFamily: 'Barlow Condensed' }}>{summary?.safety_rounds || 0}</p><p className="text-sm text-slate-600">{sv("Skyddsronder", "Safety Rounds")}</p></CardContent></Card>
              <Card><CardContent className="p-4"><div className="p-2 rounded-lg bg-red-500 w-fit"><AlertTriangle className="w-5 h-5 text-white" /></div><p className="text-2xl font-bold mt-3" style={{ fontFamily: 'Barlow Condensed' }}>{summary?.open_incidents || 0}</p><p className="text-sm text-slate-600">{sv("Öppna avvikelser", "Open Incidents")}</p></CardContent></Card>
              <Card><CardContent className="p-4"><div className="p-2 rounded-lg bg-amber-500 w-fit"><Search className="w-5 h-5 text-white" /></div><p className="text-2xl font-bold mt-3" style={{ fontFamily: 'Barlow Condensed' }}>{summary?.open_findings || 0}</p><p className="text-sm text-slate-600">{sv("Öppna iakttagelser", "Open Findings")}</p></CardContent></Card>
              <Card><CardContent className="p-4"><div className="p-2 rounded-lg bg-blue-500 w-fit"><Search className="w-5 h-5 text-white" /></div><p className="text-2xl font-bold mt-3" style={{ fontFamily: 'Barlow Condensed' }}>{summary?.total_incidents || 0}</p><p className="text-sm text-slate-600">{sv("Totalt avvikelser", "Total Incidents")}</p></CardContent></Card>
            </div>

            {/* Risk Matrix */}
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-lg" style={{ fontFamily: 'Barlow Condensed' }}>{sv("Riskmatris", "Risk Matrix")}</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full max-w-md text-center text-sm">
                    <thead>
                      <tr><th className="p-2"></th><th className="p-2 text-xs text-slate-500">{sv("Låg", "Low")}</th><th className="p-2 text-xs text-slate-500">{sv("Medel", "Med")}</th><th className="p-2 text-xs text-slate-500">{sv("Hög", "High")}</th><th className="p-2 text-xs text-slate-500">{sv("Kritisk", "Critical")}</th></tr>
                    </thead>
                    <tbody>
                      {[["high", sv("Hög", "High")], ["medium", sv("Medel", "Med")], ["low", sv("Låg", "Low")]].map(([prob, probLabel]) => (
                        <tr key={prob}>
                          <td className="p-2 text-xs text-slate-500 text-right font-medium">{probLabel}</td>
                          {["low", "medium", "high", "critical"].map(sev => {
                            const lvl = _calcRiskLevel(prob, sev);
                            const cls = { low: "bg-green-200 text-green-800", medium: "bg-amber-200 text-amber-800", high: "bg-orange-300 text-orange-900", critical: "bg-red-400 text-white" }[lvl];
                            return <td key={sev} className={`p-2 rounded ${cls} text-xs font-medium`}>{riskLevelLabel(lvl)}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex gap-4 mt-2 text-[10px] text-slate-500">
                    <span>↑ {sv("Sannolikhet", "Probability")}</span>
                    <span>→ {sv("Allvarlighet", "Severity")}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {totalInc > 0 && (
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-lg" style={{ fontFamily: 'Barlow Condensed' }}>{sv("Avvikelser per allvarlighetsgrad", "Incidents by Severity")}</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex gap-2 h-8 rounded-lg overflow-hidden">
                    {["critical", "high", "medium", "low"].map(s => {
                      const pct = totalInc > 0 ? ((bySeverity[s] || 0) / totalInc * 100) : 0;
                      if (pct === 0) return null;
                      const colors = { critical: "bg-red-500", high: "bg-orange-500", medium: "bg-amber-400", low: "bg-blue-400" };
                      return <div key={s} className={`${colors[s]} flex items-center justify-center text-white text-xs font-medium`} style={{ width: `${pct}%` }}>{bySeverity[s]}</div>;
                    })}
                  </div>
                  <div className="flex flex-wrap gap-4 mt-3">
                    {["critical", "high", "medium", "low"].map(s => (
                      <div key={s} className="flex items-center gap-2 text-sm"><div className={`w-3 h-3 rounded-sm ${{ critical: "bg-red-500", high: "bg-orange-500", medium: "bg-amber-400", low: "bg-blue-400" }[s]}`} /><span className="text-slate-600">{sevLabels[s]}: {bySeverity[s] || 0}</span></div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* RISK ASSESSMENTS TAB */}
        {tab === "risk" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-slate-600">{riskAssessments.length} {sv("riskbedömningar", "risk assessments")}</p>
              <Dialog open={raDialogOpen} onOpenChange={setRaDialogOpen}>
                <DialogTrigger asChild><Button className="btn-touch" disabled={!selectedProject} data-testid="create-ra-btn"><Plus className="w-4 h-4 mr-2" />{sv("Ny riskbedömning", "New Risk Assessment")}</Button></DialogTrigger>
                <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader><DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>{sv("Ny riskbedömning (JSA)", "New Risk Assessment (JSA)")}</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreateRA} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div><Label>{sv("Titel", "Title")} *</Label><Input value={raForm.title} onChange={e => setRaForm({...raForm, title: e.target.value})} required placeholder={sv("T.ex. Heta arbeten tak", "E.g. Hot work roof")} data-testid="ra-title" /></div>
                      <div><Label>{sv("Arbetstyp", "Work type")}</Label><Input value={raForm.work_type} onChange={e => setRaForm({...raForm, work_type: e.target.value})} placeholder={sv("T.ex. Takarbete", "E.g. Roofing")} /></div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div><Label>{sv("Plats", "Location")}</Label><Input value={raForm.location} onChange={e => setRaForm({...raForm, location: e.target.value})} placeholder={sv("T.ex. Byggnad A, Våning 3", "E.g. Building A, Floor 3")} /></div>
                      <div><Label>{sv("Ansvarig", "Responsible")}</Label><Input value={raForm.responsible} onChange={e => setRaForm({...raForm, responsible: e.target.value})} placeholder={sv("Namn på ansvarig person", "Name of responsible person")} /></div>
                    </div>
                    <div><Label>{sv("Beskrivning", "Description")}</Label><Textarea value={raForm.description} onChange={e => setRaForm({...raForm, description: e.target.value})} placeholder={sv("Beskriv arbetet...", "Describe the work...")} className="min-h-[60px]" /></div>
                    <div>
                      <div className="flex items-center justify-between mb-2"><Label className="text-base font-semibold">{sv("Risker", "Risks")}</Label><Button type="button" variant="outline" size="sm" onClick={addRisk}><Plus className="w-3 h-3 mr-1" />{sv("Lägg till", "Add")}</Button></div>
                      {raForm.risks.map((risk, idx) => (
                        <div key={idx} className="border border-slate-200 rounded-lg p-3 mb-3 space-y-3">
                          <div className="flex justify-between items-center"><span className="text-sm font-medium text-slate-600">{sv("Risk", "Risk")} {idx + 1}</span>
                            {raForm.risks.length > 1 && <Button type="button" variant="ghost" size="sm" onClick={() => removeRisk(idx)}><Trash2 className="w-4 h-4 text-red-400" /></Button>}
                          </div>
                          <Input value={risk.hazard} onChange={e => updateRisk(idx, "hazard", e.target.value)} placeholder={sv("Fara / Riskmoment", "Hazard")} />
                          <Input value={risk.consequence} onChange={e => updateRisk(idx, "consequence", e.target.value)} placeholder={sv("Konsekvens", "Consequence")} />
                          <Input value={risk.mitigation} onChange={e => updateRisk(idx, "mitigation", e.target.value)} placeholder={sv("Åtgärd / Skyddsåtgärd", "Mitigation")} />
                          <div className="grid grid-cols-2 gap-2">
                            <Select value={risk.probability} onValueChange={v => updateRisk(idx, "probability", v)}><SelectTrigger><SelectValue placeholder={sv("Sannolikhet", "Probability")} /></SelectTrigger><SelectContent><SelectItem value="low">{sv("Låg", "Low")}</SelectItem><SelectItem value="medium">{sv("Medel", "Medium")}</SelectItem><SelectItem value="high">{sv("Hög", "High")}</SelectItem></SelectContent></Select>
                            <Select value={risk.severity} onValueChange={v => updateRisk(idx, "severity", v)}><SelectTrigger><SelectValue placeholder={sv("Allvarlighet", "Severity")} /></SelectTrigger><SelectContent><SelectItem value="low">{sv("Låg", "Low")}</SelectItem><SelectItem value="medium">{sv("Medel", "Medium")}</SelectItem><SelectItem value="high">{sv("Hög", "High")}</SelectItem><SelectItem value="critical">{sv("Kritisk", "Critical")}</SelectItem></SelectContent></Select>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Button type="submit" className="w-full" disabled={submitting} data-testid="ra-submit">
                      {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}{sv("Skapa riskbedömning", "Create Risk Assessment")}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {riskAssessments.length === 0 ? (
              <Card><CardContent className="py-12 text-center"><AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-600">{sv("Inga riskbedömningar ännu", "No risk assessments yet")}</p></CardContent></Card>
            ) : (
              <div className="space-y-3">
                {riskAssessments.map(ra => (
                  <Card key={ra.risk_assessment_id} className="hover:shadow-sm transition-shadow cursor-pointer" onClick={() => { setDetailRA(ra); setEditingRA(false); }} data-testid={`ra-card-${ra.risk_assessment_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>{ra.title}</h3>
                          <div className="flex flex-wrap gap-2 mt-1">
                            <Badge className={statusColors[ra.status]}>{statusLabels[ra.status] || ra.status}</Badge>
                            {ra.work_type && <Badge variant="outline">{ra.work_type}</Badge>}
                            {ra.location && <span className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{ra.location}</span>}
                          </div>
                          <div className="flex items-center gap-3 mt-2">
                            <span className="text-sm text-slate-500">{ra.risks?.length || 0} {sv("risker", "risks")}</span>
                            {ra.risks?.map((r, i) => (
                              <span key={i} className={`inline-block w-2.5 h-2.5 rounded-full ${riskLevelColor(r.risk_level || _calcRiskLevel(r.probability, r.severity)).split(' ')[0]}`} title={r.hazard} />
                            ))}
                          </div>
                        </div>
                        <Eye className="w-4 h-4 text-slate-400 mt-1" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* SAFETY ROUNDS TAB */}
        {tab === "safety" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-slate-600">{safetyRounds.length} {sv("skyddsronder", "safety rounds")}</p>
              <Dialog open={srDialogOpen} onOpenChange={setSrDialogOpen}>
                <DialogTrigger asChild><Button className="btn-touch" disabled={!selectedProject} data-testid="create-sr-btn"><Plus className="w-4 h-4 mr-2" />{sv("Ny skyddsrond", "New Safety Round")}</Button></DialogTrigger>
                <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader><DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>{sv("Ny skyddsrond", "New Safety Round")}</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreateSR} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div><Label>{sv("Titel", "Title")} *</Label><Input value={srForm.title} onChange={e => setSrForm({...srForm, title: e.target.value})} required placeholder={sv("T.ex. Veckovis skyddsrond", "E.g. Weekly safety round")} data-testid="sr-title" /></div>
                      <div><Label>{sv("Datum", "Date")}</Label><Input type="date" value={srForm.date} onChange={e => setSrForm({...srForm, date: e.target.value})} /></div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div><Label>{sv("Deltagare (komma-separerade)", "Participants (comma-separated)")}</Label><Input value={srForm.participants} onChange={e => setSrForm({...srForm, participants: e.target.value})} placeholder="Anna, Erik, Lisa" /></div>
                      <div><Label>{sv("Område", "Area")}</Label><Input value={srForm.area} onChange={e => setSrForm({...srForm, area: e.target.value})} placeholder={sv("T.ex. Plan 2, Ställning", "E.g. Floor 2, Scaffolding")} /></div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2"><Label className="text-base font-semibold">{sv("Iakttagelser", "Findings")}</Label><Button type="button" variant="outline" size="sm" onClick={addFinding}><Plus className="w-3 h-3 mr-1" />{sv("Lägg till", "Add")}</Button></div>
                      {srForm.findings.map((f, idx) => (
                        <div key={idx} className="border border-slate-200 rounded-lg p-3 mb-3 space-y-2">
                          <div className="flex justify-between items-center"><span className="text-sm font-medium text-slate-600">{sv("Iakttagelse", "Finding")} {idx + 1}</span>
                            {srForm.findings.length > 1 && <Button type="button" variant="ghost" size="sm" onClick={() => removeFinding(idx)}><Trash2 className="w-4 h-4 text-red-400" /></Button>}
                          </div>
                          <Textarea value={f.description} onChange={e => updateFinding(idx, "description", e.target.value)} placeholder={sv("Beskriv iakttagelsen...", "Describe the finding...")} className="min-h-[50px]" />
                          <div className="grid grid-cols-3 gap-2">
                            <Select value={f.severity} onValueChange={v => updateFinding(idx, "severity", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="low">{sv("Låg", "Low")}</SelectItem><SelectItem value="medium">{sv("Medel", "Med")}</SelectItem><SelectItem value="high">{sv("Hög", "High")}</SelectItem><SelectItem value="critical">{sv("Kritisk", "Critical")}</SelectItem></SelectContent></Select>
                            <Input value={f.responsible} onChange={e => updateFinding(idx, "responsible", e.target.value)} placeholder={sv("Ansvarig", "Responsible")} />
                            <Input type="date" value={f.due_date} onChange={e => updateFinding(idx, "due_date", e.target.value)} />
                          </div>
                        </div>
                      ))}
                    </div>
                    <Button type="submit" className="w-full" disabled={submitting} data-testid="sr-submit">
                      {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}{sv("Skapa skyddsrond", "Create Safety Round")}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {safetyRounds.length === 0 ? (
              <Card><CardContent className="py-12 text-center"><ClipboardCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-600">{sv("Inga skyddsronder ännu", "No safety rounds yet")}</p></CardContent></Card>
            ) : (
              <div className="space-y-3">
                {safetyRounds.map(sr => {
                  const openF = (sr.findings || []).filter(f => f.status === "open" || f.status === "in_progress").length;
                  return (
                    <Card key={sr.safety_round_id} className="hover:shadow-sm transition-shadow cursor-pointer" onClick={() => { setDetailSR(sr); setEditingSR(false); }} data-testid={`sr-card-${sr.safety_round_id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>{sr.title}</h3>
                            <div className="flex flex-wrap gap-2 mt-1">
                              <Badge className={statusColors[sr.status]}>{statusLabels[sr.status] || sr.status}</Badge>
                              <span className="text-xs text-slate-500 flex items-center gap-1"><CalendarDays className="w-3 h-3" />{sr.date}</span>
                              {sr.participants?.length > 0 && <span className="text-xs text-slate-500 flex items-center gap-1"><User className="w-3 h-3" />{sr.participants.length}</span>}
                              {sr.area && <span className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{sr.area}</span>}
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-sm text-slate-500">{sr.findings?.length || 0} {sv("iakttagelser", "findings")}</span>
                              {openF > 0 && <Badge className="bg-amber-100 text-amber-800 text-xs">{openF} {sv("öppna", "open")}</Badge>}
                            </div>
                          </div>
                          <Eye className="w-4 h-4 text-slate-400 mt-1" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* RA Detail Dialog */}
      <Dialog open={!!detailRA} onOpenChange={(open) => { if (!open) setDetailRA(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          {detailRA && (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Barlow Condensed' }} className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" /> {detailRA.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Badge className={statusColors[detailRA.status]}>{statusLabels[detailRA.status] || detailRA.status}</Badge>
                  {detailRA.work_type && <Badge variant="outline">{detailRA.work_type}</Badge>}
                  {detailRA.location && <Badge variant="outline" className="flex items-center gap-1"><MapPin className="w-3 h-3" />{detailRA.location}</Badge>}
                  {detailRA.responsible && <Badge variant="outline" className="flex items-center gap-1"><User className="w-3 h-3" />{detailRA.responsible}</Badge>}
                </div>
                {detailRA.description && <p className="text-sm text-slate-600">{detailRA.description}</p>}
                <div className="text-xs text-slate-400">
                  {sv("Skapad av", "Created by")} {detailRA.created_by_name || detailRA.created_by} — {new Date(detailRA.created_at).toLocaleString("sv-SE")}
                  {detailRA.approved_by_name && <span> | {sv("Godkänd av", "Approved by")} {detailRA.approved_by_name}</span>}
                </div>

                {/* Risks */}
                <div>
                  <h4 className="font-semibold text-sm mb-2">{sv("Identifierade risker", "Identified Risks")} ({detailRA.risks?.length || 0})</h4>
                  {(detailRA.risks || []).map((r, i) => {
                    const lvl = r.risk_level || _calcRiskLevel(r.probability, r.severity);
                    return (
                      <div key={i} className="border rounded-lg p-3 mb-2 space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm text-slate-800">{r.hazard || `${sv("Risk", "Risk")} ${i + 1}`}</span>
                          <Badge className={`text-xs ${riskLevelColor(lvl)}`}>{riskLevelLabel(lvl)}</Badge>
                        </div>
                        {r.consequence && <p className="text-xs text-slate-500">{sv("Konsekvens:", "Consequence:")} {r.consequence}</p>}
                        {r.mitigation && <p className="text-xs text-green-700">{sv("Åtgärd:", "Mitigation:")} {r.mitigation}</p>}
                        <div className="flex gap-3 text-[10px] text-slate-400">
                          <span>{sv("Sannolikhet:", "Probability:")} {sevLabels[r.probability] || r.probability}</span>
                          <span>{sv("Allvarlighet:", "Severity:")} {sevLabels[r.severity] || r.severity}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Workflow Actions */}
                <div className="flex flex-wrap gap-2 pt-2 border-t">
                  {detailRA.status === "draft" && (
                    <>
                      <Button size="sm" onClick={async () => { if (await handleUpdateRA(detailRA.risk_assessment_id, { status: "under_review" })) toast.success(sv("Skickad till granskning", "Sent for review")); }} data-testid="ra-send-review">
                        <ArrowRight className="w-4 h-4 mr-1" />{sv("Skicka till granskning", "Send for review")}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteRA(detailRA.risk_assessment_id)} data-testid="ra-delete">
                        <Trash2 className="w-4 h-4 mr-1 text-red-500" />{sv("Radera", "Delete")}
                      </Button>
                    </>
                  )}
                  {detailRA.status === "under_review" && (
                    <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={async () => { if (await handleUpdateRA(detailRA.risk_assessment_id, { status: "approved" })) toast.success(sv("Godkänd!", "Approved!")); }} data-testid="ra-approve">
                      <CheckCircle className="w-4 h-4 mr-1" />{sv("Godkänn", "Approve")}
                    </Button>
                  )}
                  {detailRA.status === "approved" && (
                    <Badge className="bg-green-100 text-green-700 px-4 py-2">{sv("Denna riskbedömning är godkänd", "This risk assessment is approved")}</Badge>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* SR Detail Dialog */}
      <Dialog open={!!detailSR} onOpenChange={(open) => { if (!open) setDetailSR(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          {detailSR && (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Barlow Condensed' }} className="flex items-center gap-2">
                  <ClipboardCheck className="w-5 h-5 text-teal-500" /> {detailSR.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Badge className={statusColors[detailSR.status]}>{statusLabels[detailSR.status] || detailSR.status}</Badge>
                  <Badge variant="outline" className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{detailSR.date}</Badge>
                  {detailSR.area && <Badge variant="outline" className="flex items-center gap-1"><MapPin className="w-3 h-3" />{detailSR.area}</Badge>}
                </div>
                {detailSR.participants?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {detailSR.participants.map((p, i) => <Badge key={i} variant="outline" className="text-xs">{p}</Badge>)}
                  </div>
                )}
                <div className="text-xs text-slate-400">
                  {sv("Skapad av", "Created by")} {detailSR.created_by_name || detailSR.created_by} — {new Date(detailSR.created_at).toLocaleString("sv-SE")}
                </div>

                {/* Findings */}
                <div>
                  <h4 className="font-semibold text-sm mb-2">{sv("Iakttagelser", "Findings")} ({detailSR.findings?.length || 0})</h4>
                  {(detailSR.findings || []).map((f, i) => (
                    <div key={f.finding_id || i} className={`border rounded-lg p-3 mb-2 ${f.status === 'resolved' ? 'bg-green-50 border-green-200' : ''}`} data-testid={`finding-${f.finding_id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="text-sm text-slate-800">{f.description}</p>
                          <div className="flex flex-wrap gap-2 mt-1">
                            <Badge className={`text-[10px] ${sevColors[f.severity]}`}>{sevLabels[f.severity]}</Badge>
                            <Badge className={`text-[10px] ${statusColors[f.status]}`}>{statusLabels[f.status] || f.status}</Badge>
                            {f.responsible && <span className="text-[10px] text-slate-500 flex items-center gap-0.5"><User className="w-2.5 h-2.5" />{f.responsible}</span>}
                            {f.due_date && <span className="text-[10px] text-slate-500 flex items-center gap-0.5"><CalendarDays className="w-2.5 h-2.5" />{f.due_date}</span>}
                          </div>
                          {f.action_taken && <p className="text-xs text-green-700 mt-1">{sv("Åtgärd:", "Action:")} {f.action_taken}</p>}
                        </div>
                        {f.status !== "resolved" && detailSR.status !== "completed" && (
                          <div className="flex gap-1">
                            {f.status === "open" && (
                              <Button variant="ghost" size="sm" className="text-xs h-7" onClick={(e) => { e.stopPropagation(); handleUpdateFinding(detailSR.safety_round_id, f.finding_id, { status: "in_progress" }); }} data-testid={`finding-progress-${f.finding_id}`}>
                                {sv("Pågår", "Progress")}
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" className="text-xs h-7 text-green-600" onClick={(e) => {
                              e.stopPropagation();
                              const action = prompt(sv("Beskriv åtgärd:", "Describe action taken:"));
                              if (action !== null) handleUpdateFinding(detailSR.safety_round_id, f.finding_id, { status: "resolved", action_taken: action });
                            }} data-testid={`finding-resolve-${f.finding_id}`}>
                              <CheckCircle className="w-3.5 h-3.5 mr-0.5" />{sv("Åtgärda", "Resolve")}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Workflow Actions */}
                <div className="flex flex-wrap gap-2 pt-2 border-t">
                  {detailSR.status === "draft" && (
                    <>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={async () => { if (await handleUpdateSR(detailSR.safety_round_id, { status: "completed" })) toast.success(sv("Slutförd!", "Completed!")); }} data-testid="sr-complete">
                        <CheckCircle className="w-4 h-4 mr-1" />{sv("Slutför skyddsrond", "Complete safety round")}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteSR(detailSR.safety_round_id)} data-testid="sr-delete">
                        <Trash2 className="w-4 h-4 mr-1 text-red-500" />{sv("Radera", "Delete")}
                      </Button>
                    </>
                  )}
                  {detailSR.status === "completed" && (
                    <Badge className="bg-green-100 text-green-700 px-4 py-2">{sv("Denna skyddsrond är slutförd", "This safety round is completed")}</Badge>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

function _calcRiskLevel(probability, severity) {
  const matrix = {
    "low-low": "low", "low-medium": "low", "low-high": "medium", "low-critical": "high",
    "medium-low": "low", "medium-medium": "medium", "medium-high": "high", "medium-critical": "high",
    "high-low": "medium", "high-medium": "high", "high-high": "high", "high-critical": "critical",
  };
  return matrix[`${probability}-${severity}`] || "medium";
}
